import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { PremiumBadge } from "@/components/PremiumBadge";

/**
 * PremiumFeatureCard — one feature tile.
 *
 * Two variants:
 *   compact — icon + title + inline "Wkrótce" badge (used in Settings grid, 2/3 cols).
 *   full    — icon + title + description + badge in top-right (used on /premium).
 *
 * `Icon` is a Lucide component. `children` may be used in full variant for
 * extras (e.g. the 5 day-mode chips inside the "Tryby dnia" feature).
 */
export function PremiumFeatureCard({
  Icon,
  titleKey,
  descriptionKey,
  variant = "compact",
  testId,
  children,
}) {
  const { t } = useI18n();

  if (variant === "compact") {
    return (
      <div
        className="dc-card p-3 flex flex-col gap-2 h-full"
        data-testid={testId}
      >
        <div className="flex items-center gap-2">
          <span
            className="flex-none w-7 h-7 rounded-lg flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(244,201,93,0.18), rgba(167,139,250,0.14))",
            }}
          >
            <Icon size={14} className="text-[#F4C95D]" strokeWidth={1.7} />
          </span>
          <h3
            className="text-[13px] font-medium leading-tight text-[#F8FAFC]"
            style={{ letterSpacing: "-0.005em" }}
          >
            {t(titleKey)}
          </h3>
        </div>
        <div className="mt-auto">
          <PremiumBadge />
        </div>
      </div>
    );
  }

  // full
  return (
    <article
      className="dc-card relative p-5"
      data-testid={testId}
    >
      <div className="absolute top-4 right-4">
        <PremiumBadge />
      </div>
      <div className="flex items-start gap-3 pr-20">
        <span
          className="flex-none w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, rgba(244,201,93,0.22), rgba(167,139,250,0.18))",
          }}
        >
          <Icon size={18} className="text-[#F4C95D]" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <h3
            className="text-[15px] font-medium text-[#F8FAFC] leading-snug"
            style={{ letterSpacing: "-0.01em" }}
          >
            {t(titleKey)}
          </h3>
          {descriptionKey ? (
            <p className="mt-1.5 text-[13px] text-[#CBD5E1] leading-relaxed">
              {t(descriptionKey)}
            </p>
          ) : null}
          {children}
        </div>
      </div>
    </article>
  );
}
